import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { createBlockApi } from '@skyeng/libs/shared/blocks/core';
import { BlockGroupComponent, getBlockContentGroupId } from '@skyeng/libs/shared/blocks/shared';
import { DecorateUntilDestroy, filterEmpty, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { Store } from '@skyeng/libs/shared/store';
import { DsDialogService } from '@skyeng/libs/shared/ui-kit-v2/dialog';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap, take, withLatestFrom } from 'rxjs/operators';
import { PythonBlockApi, PythonModel, PythonScore } from '../../exercise';
import { TestCase } from '../../interface';
import { PythonRunnerService } from '../../service/python-runner';
import { ResetDialogComponent } from '../reset-dialog/reset-dialog.component';
import { registerTranslationsManuallyStatic } from '@skyeng/libs/shared/i18n';
import { TranslocoService } from '@ngneat/transloco';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import clArrowDown from './images/cl-arrow-down.svg';
import testCaseOk from './images/test-case-ok.svg';
import testCaseError from './images/test-case-error.svg';
import ok from './images/ok.svg';
import play from './images/play.svg';
import skyproPythonRefresh from './images/skypro-python-refresh.svg';
import entranslation from './translation/en.json';
import rutranslation from './translation/ru.json';

enum TestCasesRunType {
  testCase = 'testCase',
  submit = 'submit',
}

export interface TestCaseRunResult {
  inputs: string[];
  expectedConsoleOutput: string;
  actualConsoleOutput: string;
  isSuccessCheck: boolean;
}

const initialState = {
  initialCode: undefined as string | undefined,
  currentCode: '',
  beforeCode: '',
  afterCode: '',
  resultMessage: '',
  // execution
  isExecuting: false,
  isTestCasesSubmitExecuting: false,
  isTestCaseCheckExecuting: false,
  executionTime: 0,
  // test cases
  testCases: [] as TestCase[],
  testCasesRunResult: [] as TestCaseRunResult[],
};

// TODO: Разделить блок на блок + абстрактный компонент тренажера?
@DecorateUntilDestroy()
@Component({
  selector: 'skypro-python-exercise',
  templateUrl: 'python-exercise.html',
  styleUrls: ['python-exercise.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PythonExerciseComponent implements OnInit, OnDestroy {
  @Input() id: string;
  // Если true, то в редакторе включится автозакрытие скобок и кавычек
  @Input() closeBracket: string;

  isAutoCloseBracket: boolean;

  private blockApi: PythonBlockApi;

  private store = new Store<typeof initialState, { codeChanged: string }>(initialState, { queueUpdate: true });

  store$ = this.store.select();
  code$ = this.store.select('currentCode');

  private currentRun$ = new Subject<TestCasesRunType>();
  isCurrentRunCheck$ = this.currentRun$.pipe(map(runType => runType === TestCasesRunType.testCase));
  isCurrentRunSubmit$ = this.currentRun$.pipe(map(runType => runType === TestCasesRunType.submit));

  isResultMessageVisible$: Observable<boolean> = this.store.select('testCasesRunResult').pipe(
    withLatestFrom(this.isCurrentRunSubmit$, this.store.select('resultMessage')),
    map(([testCasesRunResult, isSubmitRunType, resultMessage]) => {
      const isEveryCaseSuccess =
        !!testCasesRunResult?.length && testCasesRunResult.every(testCase => testCase.isSuccessCheck);

      return !!resultMessage?.length && isSubmitRunType && isEveryCaseSuccess;
    }),
  );

  arrowDownIcon = clArrowDown;
  testCaseOkIcon = testCaseOk;
  testCaseErrorIcon = testCaseError;
  resultMessageIcon = ok;
  playIcon = play;
  refreshIcon = skyproPythonRefresh;

  // Без этого не сохраняются ответы в store-blocks
  private blockGroupComponent = inject(BlockGroupComponent<{}>, { optional: true });
  private pythonRunnerService = inject(PythonRunnerService);
  private dsDialogService = inject(DsDialogService);
  private translocoService = inject(TranslocoService);

  constructor() {
    registerTranslationsManuallyStatic('skyproLibsBlocksPythonExercise', this.translocoService, {
      en: entranslation,
      ru: rutranslation,
    });
  }

  ngOnInit(): void {
    this.pythonRunnerService.start({ warmupSyncRunner: false });
    // reference: libs/shared/blocks/example-block/src/component/example-block/example-block.ts
    this.blockApi = createBlockApi({
      block: { blockId: this.id, contentGroupId: getBlockContentGroupId(this.blockGroupComponent) },
      modelConstructor: PythonModel,
      scoreConstructor: PythonScore,
    });

    this.blockApi.init();

    combineLatest([
      this.store.select('initialCode').pipe(filterEmpty()),
      this.blockApi.model.initialStateLoaded$.pipe(switchMap(() => this.blockApi.model.selectState('code', undefined))),
    ])
      .pipe(
        debounceTime(0),
        map(([initialCode, cachedCode]) => {
          return cachedCode ?? initialCode;
        }),
        take(1),
        takeUntilDestroyed(this),
      )
      .subscribe(code => this.store.updateFields({ currentCode: code }));

    this.store
      .on('codeChanged')
      .pipe(debounceTime(300), takeUntilDestroyed(this))
      .subscribe(() => {
        this.blockApi.model.updateState({
          code: this.store.get('currentCode'),
        });
      });

    this.isAutoCloseBracket = coerceBooleanProperty(this.closeBracket);
  }

  ngOnDestroy(): void {
    this.blockApi.destroy();
    this.pythonRunnerService.stop();
  }

  // Запуск без проверок, не пересчитывает скоринг, не засчитывается как ответ.
  singleRun(): void {
    this.currentRun$.next(TestCasesRunType.testCase);
    const testCase = this.store.get('testCases')[0];

    if (!testCase) {
      const message = this.translocoService.translate('skyproLibsBlocksPythonExercise.noTestCases');
      alert(`${message}`);
      return;
    }

    this.store.updateFields({
      isTestCaseCheckExecuting: true,
    });

    this.runTestCases([testCase])
      .pipe(take(1), takeUntilDestroyed(this))
      .subscribe(() => {
        this.store.updateFields({
          isTestCaseCheckExecuting: false,
        });
      });
  }

  // Запуск с проверками, не засчитывается как ответ
  checkAnswer(): void {
    this.currentRun$.next(TestCasesRunType.submit);
    const testCases = this.store.get('testCases');

    if (testCases.length === 0) {
      const message = this.translocoService.translate('skyproLibsBlocksPythonExercise.noTestCases');
      alert(`${message}`);
      return;
    }

    this.store.updateFields({
      isTestCasesSubmitExecuting: true,
    });

    this.runTestCases(testCases)
      .pipe(take(1), takeUntilDestroyed(this))
      .subscribe(results => {
        this.store.updateFields({
          isTestCasesSubmitExecuting: false,
        });
        const testSuitePassed = results.every(({ isSuccessCheck }) => isSuccessCheck);

        this.blockApi.model.addAnswer({
          value: {
            testSuitePassed,
          },
        });
      });
  }

  addTestCase(testCase: TestCase): void {
    this.store.updateFields({
      testCases: [...this.store.get('testCases'), testCase],
    });
  }

  addInitialCode(code: string): void {
    this.store.updateField('initialCode', code);
  }

  addBeforeCode(code: string): void {
    this.store.updateField('beforeCode', code);
  }

  addAfterCode(code: string): void {
    this.store.updateField('afterCode', code);
  }

  addResultMessage(message: string): void {
    this.store.updateField('resultMessage', message);
  }

  getPreparedCode(): Observable<string> {
    const beforeCode = this.store.get('beforeCode');
    const afterCode = this.store.get('afterCode');

    return this.code$.pipe(
      map(userCode => {
        return `
${beforeCode}
${userCode}
${afterCode}
`;
      }),
    );
  }

  onCodeChange(code: string): void {
    this.store.updateFields({ currentCode: code });
    this.store.fire('codeChanged', code);
  }

  showResetCodeModal(): void {
    this.dsDialogService
      .open(ResetDialogComponent)
      .onClose()
      .pipe(takeUntilDestroyed(this))
      .subscribe(({ rejected }) => {
        if (rejected) {
          return;
        }

        this.resetCode();
      });
  }

  private resetCode(): void {
    const initialCode = this.store.get('initialCode');
    if (!initialCode) {
      return;
    }

    this.onCodeChange(initialCode);
  }

  private runTestCases(testCases: TestCase[]): Observable<TestCaseRunResult[]> {
    const startTime = Date.now();
    this.store.updateFields({
      testCasesRunResult: [],
      isExecuting: true,
      executionTime: 0,
    });

    return this.getPreparedCode().pipe(
      switchMap(preparedCode => {
        const checks = testCases.map(testCase => this.runTestCase(preparedCode, testCase));
        return combineLatest(checks).pipe(
          tap(testCasesRunResult =>
            this.store.updateFields({
              testCasesRunResult,
              isExecuting: false,
              executionTime: Date.now() - startTime,
            }),
          ),
        );
      }),
    );
  }

  private runTestCase(userCode: string, testCase: TestCase): Observable<TestCaseRunResult> {
    const warningMessage = this.translocoService.translate('skyproLibsBlocksPythonExercise.tooManyInputs');
    const inputsForPython = '[' + testCase.inputs.map(input => `'${input}'`).join(',') + ']';

    const checkCode = `# check code
import json
inputs = ${inputsForPython}
initialInputsLength = len(inputs);
def input(param = ""):
  if len(inputs) == 0:
    raise ValueError("${warningMessage} " + str(initialInputsLength))
  input = inputs.pop(0);
  return input`;

    const code = `
${checkCode}
${userCode}
    `;
    const expectedConsoleOutput = testCase.output;
    const inputs = testCase.inputs;

    return this.pythonRunnerService.run(code, { timeout: 3000 }).pipe(
      map(({ consoleOutput }) => {
        // Питон всегда добавляет \n в конце stdout
        const actualConsoleOutput = consoleOutput.replace(/\n$/, '');

        const isSuccessCheck = expectedConsoleOutput === actualConsoleOutput;

        return { isSuccessCheck, expectedConsoleOutput, inputs, actualConsoleOutput };
      }),
      catchError(error => {
        return of({
          isSuccessCheck: false,
          expectedConsoleOutput,
          inputs,
          actualConsoleOutput: error.toString(),
        });
      }),
    );
  }
}
